import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import axios from "axios";
import { toast } from "react-toastify";
import { fetchLogoSrc } from "../../../assets/js/common";

const preloadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = () => reject(new Error("Failed to load logo image"));
  });

export const fitImageToBox = (src, boxW, boxH) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = boxW;
      canvas.height = boxH;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, boxW, boxH);

      const scale = Math.min(boxW / img.width, boxH / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (boxW - w) / 2;
      const y = (boxH - h) / 2;
      ctx.drawImage(img, x, y, w, h);

      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Failed to load signature image"));
    img.src = src;
  });

export const handleUploadPDF = async (
  invoiceRef,
  formData,
  proposalVersionId,
  setBorderVisible
) => {

  try {
    setBorderVisible(false);
    await new Promise((resolve) => setTimeout(resolve, 100));

    const input = invoiceRef.current;
    if (!input) {
      console.error("Invoice element not found");
      return;
    }

    const logoSrc = await fetchLogoSrc(formData?.customerId);
    await preloadImage(logoSrc);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const padding = 10;

    const cards = input.querySelectorAll(".card");

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const wrapper = document.createElement("div");

      const style = document.createElement("style");
      style.innerHTML = `
        * {
          font-family: Arial, sans-serif !important;
          font-size: 13px !important;
          line-height: 1.4 !important;
        }
        .fs-10 { font-size: 13px !important; }
        .client-sig-col { margin-top: 0 !important; }
      `;
      wrapper.appendChild(style);

      wrapper.style.position = "absolute";
      wrapper.style.left = "-9999px";
      wrapper.style.top = "0";
      wrapper.style.width = `${card.clientWidth}px`;
      wrapper.style.background = "white";
      wrapper.style.padding = "10px";

      const clonedCard = card.cloneNode(true);

      clonedCard.querySelectorAll(".sig-canvas-wrapper").forEach((el) => {
        el.style.border = "none";
        el.style.outline = "none";
        el.style.boxShadow = "none";
        el.style.background = "transparent";
      });

      clonedCard.querySelectorAll(".sig-icon-btn").forEach((el) => {
        const container = el.closest(".position-absolute");
        if (container) container.remove();
        else el.remove();
      });

      clonedCard.querySelectorAll("textarea").forEach((textarea) => {
        const p = document.createElement("p");
        p.className = "fs-10 mb-0";
        p.textContent = textarea.value || "";
        textarea.replaceWith(p);
      });

      clonedCard.querySelectorAll(".text-danger small").forEach((el) => {
        el.closest("p")?.remove();
      });

      clonedCard.querySelectorAll(".justify-content-end.mb-4").forEach((el) => {
        el.remove();
      });

      const imgs = clonedCard.querySelectorAll("img");
      for (const img of imgs) {
        const inlineWidth = img.style.width;
        const inlineHeight = img.style.height;
        const parentHasSigWrapper = !!img.closest(".sig-canvas-wrapper");

        const isSignature =
          parentHasSigWrapper ||
          (inlineWidth === "150px" && inlineHeight === "60px");

        if (isSignature) {
          try {
            const fitted = await fitImageToBox(img.src, 150, 60);
            img.src = fitted;
          } catch (err) {
            console.warn("Signature fit failed, falling back to CSS scaling:", err);
          }
          // Box is already baked into the pixels, just lock the display size
          img.style.setProperty("width", "150px", "important");
          img.style.setProperty("height", "60px", "important");
          img.style.setProperty("display", "block", "important");
        } else {
          img.style.setProperty("max-width", "150px", "important");
          img.style.setProperty("height", "auto", "important");
        }
      }

      wrapper.appendChild(clonedCard);
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        scale: 1.2,
        useCORS: true,
        imageTimeout: 0,
        logging: false,
      });

      document.body.removeChild(wrapper);

      const imgData = canvas.toDataURL("image/jpeg", 0.75);

      const usableWidth = pdfWidth - padding * 2;
      const usableHeight = pdfHeight - padding * 2;
      const imgRatio = canvas.width / canvas.height;
      const pdfRatio = usableWidth / usableHeight;

      let imgWidth, imgHeight;
      if (imgRatio > pdfRatio) {
        imgWidth = usableWidth;
        imgHeight = usableWidth / imgRatio;
      } else {
        imgHeight = usableHeight;
        imgWidth = usableHeight * imgRatio;
      }

      const x = (pdfWidth - imgWidth) / 2;
      const y = padding;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);
    }

    const pdfBlob = pdf.output("blob");

    const docData = new FormData();
    docData.append(
      "file",
      pdfBlob,
      `${formData.workType || formData.WorkType}.pdf`
    );

    const proposalId =
      formData?.proposalId ||
      formData?.ProposalId ||
      formData?.proposal?.ProposalId;

    const currentVersionId =
      proposalVersionId ||
      formData?.proposalVersionId ||
      formData?.CurrentVersionId ||
      formData?.proposal?.CurrentVersionId;


    docData.append("proposalId",proposalId);
    docData.append("proposalVersionId",currentVersionId);

    const relationId = formData.selectedClientData?.RelationId ?? formData.RelationId;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/blobRoute/uploadBlob/${relationId}`,
      docData,
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    );

    return response.data.blobUrl;
  } catch (error) {
    console.error("Error generating and uploading PDF:", error);
    const reason = error.response?.data;
    if (reason?.code) {
      toast.error(reason.message || reason.error, { position: "top-right", autoClose: 6000 });
    }
    return null;
  } finally {
    setBorderVisible(true);
  }
};

export const handlePreviewPDF = async (invoiceRef, setBorderVisible) => {
  setBorderVisible(false);
  const input = invoiceRef.current;
  if (!input) {
    console.error("Invoice element not found");
    return;
  }

  // Function to preload image
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = resolve;
      img.onerror = reject;
    });
  };

  try {
    const logoSrc = await fetchLogoSrc();
    await preloadImage(logoSrc);
    // await preloadImage(logotest);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Get all sections including page-breaks
    const sections = input.querySelectorAll(".page-break");
    let startElement = input.firstElementChild;
    let pageAdded = false;

    for (let i = 0; i <= sections.length; i++) {
      let endElement = sections[i] || null;
      const tempWrapper = document.createElement("div");
      tempWrapper.style.position = "absolute";
      tempWrapper.style.left = "-9999px";
      tempWrapper.style.top = "0";
      tempWrapper.style.width = `${input.clientWidth}px`;
      tempWrapper.style.background = "white";
      tempWrapper.style.padding = "10px";
      tempWrapper.style.overflow = "hidden"; // Prevent content overflow
      document.body.appendChild(tempWrapper);

      let currentElement = startElement;
      while (currentElement && currentElement !== endElement) {
        tempWrapper.appendChild(currentElement.cloneNode(true));
        currentElement = currentElement.nextElementSibling;
      }

      if (!tempWrapper.innerHTML.trim()) {
        console.warn("Skipping empty section.");
        document.body.removeChild(tempWrapper);
        continue;
      }

      const canvas = await html2canvas(tempWrapper, { scale: 2 });
      document.body.removeChild(tempWrapper);
      const imgData = canvas.toDataURL("image/png");

      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Scale down if content exceeds the page height
      if (imgHeight > pageHeight) {
        const scaleFactor = pageHeight / imgHeight;
        imgHeight *= scaleFactor; // Reduce height
      }

      if (i > 0 || pageAdded) pdf.addPage();
      const padding = 10;
      const usableWidth = pdfWidth - padding * 2;
      const usableHeight = pageHeight - padding * 2;
      const scaledHeight = Math.min(
        (canvas.height * usableWidth) / canvas.width,
        usableHeight
      );

      pdf.addImage(imgData, "PNG", padding, padding, usableWidth, scaledHeight);

      pageAdded = true;

      startElement = endElement ? endElement : null;
      if (!startElement) break;
    }

    const pdfBlob = pdf.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, "_blank");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }

  setBorderVisible(true);
};

export const handleDownloadPDF = async (invoiceRef, pdfName) => {
  try {
    const input = invoiceRef.current;
    if (!input) {
      console.error("Invoice element not found");
      return;
    }

    const logoSrc = await fetchLogoSrc();
    await preloadImage(logoSrc);
    // await preloadImage(logotest); // Preload the logo if necessary

    // Set custom page size: A3 (297 x 420 mm) or custom (increase height to 500mm)
    const pdf = new jsPDF("p", "mm", "a4"); // Increase height to 500mm (adjust as needed)
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Get all sections including page-breaks
    const sections = input.querySelectorAll(".page-break");

    let startElement = input.firstElementChild;
    let pageAdded = false;

    for (let i = 0; i <= sections.length; i++) {
      let endElement = sections[i] || null;
      // Create a temporary wrapper to capture the section
      const tempWrapper = document.createElement("div");
      tempWrapper.style.position = "absolute";
      tempWrapper.style.left = "-9999px";
      tempWrapper.style.top = "0";
      tempWrapper.style.width = `${input.clientWidth}px`;
      tempWrapper.style.background = "white"; // Ensure background color consistency
      tempWrapper.style.padding = "10px";
      document.body.appendChild(tempWrapper);

      let currentElement = startElement;
      while (currentElement && currentElement !== endElement) {
        tempWrapper.appendChild(currentElement.cloneNode(true));
        currentElement = currentElement.nextElementSibling;
      }

      if (!tempWrapper.innerHTML.trim()) {
        console.warn("Skipping empty section.");
        document.body.removeChild(tempWrapper);
        continue;
      }

      // Convert tempWrapper to canvas
      const canvas = await html2canvas(tempWrapper, { scale: 2 });
      document.body.removeChild(tempWrapper);

      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (i > 0 || pageAdded) pdf.addPage(); // Add a new page for each section after the first
      const padding = 10;
      const usableWidth = pdfWidth - padding * 2;
      const usableHeight = pageHeight - padding * 2;
      const scaledHeight = Math.min(
        (canvas.height * usableWidth) / canvas.width,
        usableHeight
      );

      pdf.addImage(
        imgData,
        "JPEG",
        padding,
        padding,
        usableWidth,
        scaledHeight
      );
      pageAdded = true;

      startElement = endElement ? endElement : null;
      if (!startElement) break;
    }

    pdf.save(`${pdfName}.pdf`);
  } catch (error) {
    console.error("Error generating PDF for download:", error);
  }
};
